/**
 * WorkspaceGate — ensures the user lands in a usable state.
 *
 * After auth but before any workspace-dependent page (Dashboard, Studio,
 * Library, etc.), this guard checks:
 *
 *   1. If cache + server both say we have NO workspaces → redirect to
 *      /workspaces, which shows the create-workspace wizard. Prevents
 *      new users from landing on half-rendered pages with empty data.
 *
 *   2. If we DO have workspaces but none is active yet → block render
 *      for the single frame it takes the auto-picker in useWorkspace
 *      to adopt one. This is imperceptible in practice.
 *
 *   3. If we have an active workspace → render the page.
 *
 * Exempts /workspaces + /settings so the user can still get there when
 * they have no workspaces (or want to manage them).
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";
import LoadingFallback from "./LoadingFallback";
import { ROUTES } from "@/types";

const EXEMPT_PATHS: string[] = [
  ROUTES.WORKSPACES,
  ROUTES.SETTINGS,
];

interface WorkspaceGateProps {
  children: ReactNode;
}

export default function WorkspaceGate({ children }: WorkspaceGateProps) {
  const { workspaces, active, loading } = useWorkspace();
  const { pathname } = useLocation();

  const isExempt = EXEMPT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (isExempt) return <>{children}</>;

  // Cold boot, no cached list yet — show a brief loader. Warm boots skip
  // this because useWorkspace seeds from cache synchronously.
  if (loading && workspaces.length === 0) {
    return <LoadingFallback />;
  }

  // Authed but no workspaces — route them through the Phase 0 onboarding
  // flow (2-step wizard outside the AppLayout). Old /workspaces was a
  // power-user CRUD surface; the new flow is friendlier for first-run.
  if (!loading && workspaces.length === 0) {
    return <Navigate to={ROUTES.ONBOARDING_WORKSPACE} replace />;
  }

  // Edge case: list exists but no active yet (useWorkspace auto-picks on
  // next tick). Render the loader rather than the page to avoid flicker.
  if (!active) {
    return <LoadingFallback />;
  }

  return <>{children}</>;
}
